import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Helper function to format week range
const formatWeekRange = (weekStart, weekEnd) => {
  const start = new Date(weekStart);
  const end = new Date(weekEnd);
  const startFormatted = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endFormatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${startFormatted} - ${endFormatted}`;
};

const AmbassadorOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/campus/ambassador-overview/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching ambassador stats:', err);
      setError('Failed to load statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const { totalUsersOnboarded, weeklyAverage, totalAmbassadors, weeklyTarget, last4Weeks, previousWeekTopPerformer } = stats || {};

  return (
    <>
      {/* First Row: Three Equal Cards */}
      <div className="row g-4 mb-4">
        {/* Total Users Onboarded */}
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card shadow-sm border rounded-xl">
            <div className="p-4">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(76, 29, 149, 0.1)' }}>
                  <i className="bi bi-people-fill text-primary" style={{ fontSize: '1.25rem' }}></i>
                </div>
                <p className="text-muted mb-0 small fw-medium" style={{ fontSize: '0.875rem' }}>Total Users Onboarded</p>
              </div>
              <div className="d-flex align-items-baseline gap-2">
                <h2 className="mb-0 fw-bold" style={{ fontSize: '1.875rem', color: '#1e293b' }}>{totalUsersOnboarded || 0}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Average */}
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card shadow-sm border rounded-xl">
            <div className="p-4">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(234, 88, 12, 0.1)' }}>
                  <i className="bi bi-graph-up text-warning" style={{ fontSize: '1.25rem' }}></i>
                </div>
                <p className="text-muted mb-0 small fw-medium" style={{ fontSize: '0.875rem' }}>Weekly Average</p>
              </div>
              <div className="d-flex align-items-baseline gap-2">
                <h2 className="mb-0 fw-bold text-success" style={{ fontSize: '1.875rem', color: '#1e293b' }}>{weeklyAverage || 0}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Total Ambassadors */}
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card shadow-sm border rounded-xl">
            <div className="p-4">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(76, 29, 149, 0.1)' }}>
                  <i className="bi bi-person-badge text-primary" style={{ fontSize: '1.25rem' }}></i>
                </div>
                <p className="text-muted mb-0 small fw-medium" style={{ fontSize: '0.875rem' }}>Total Ambassadors</p>
              </div>
              <div className="d-flex align-items-baseline gap-2">
                <h2 className="mb-0 fw-bold" style={{ fontSize: '1.875rem', color: '#1e293b' }}>{totalAmbassadors || 0}</h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row: Full Width Weekly Target Card */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border rounded-xl">
            <div className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(234, 88, 12, 0.1)' }}>
                    <i className="bi bi-trophy text-warning" style={{ fontSize: '1.25rem' }}></i>
                  </div>
                  <div>
                    <p className="text-muted mb-0 small fw-medium" style={{ fontSize: '0.875rem' }}>Weekly Target</p>
                    {weeklyTarget?.deadlineDate && (
                      <div className="d-flex align-items-center gap-1 mt-1">
                        <i className="bi bi-calendar-event text-warning" style={{ fontSize: '0.75rem' }}></i>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>Deadline: {weeklyTarget.deadlineDate}</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-baseline gap-2 mb-3">
                <h2 className="mb-0 fw-bold" style={{ fontSize: '1.875rem' }}>
                  <span className={weeklyTarget?.achieved >= weeklyTarget?.expected ? 'text-success' : 'text-warning'}>
                    {weeklyTarget?.achieved || 0}/{weeklyTarget?.expected || 0}
                  </span>
                </h2>
              </div>
              {weeklyTarget && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-medium text-dark">Achieved vs Expected</span>
                    <span className="fw-bold text-primary">
                      {weeklyTarget.achieved || 0} / {weeklyTarget.expected || 0}
                    </span>
                  </div>
                  <div 
                    className="rounded-pill overflow-hidden"
                    style={{ 
                      height: '16px', 
                      backgroundColor: '#f1f5f9',
                      width: '100%'
                    }}
                  >
                    <div 
                      className="h-100 rounded-pill"
                      style={{ 
                        width: `${Math.min(100, ((weeklyTarget.achieved || 0) / (weeklyTarget.expected || 1)) * 100)}%`,
                        background: 'linear-gradient(to right, #4C1D95, #EA580C)',
                        transition: 'width 0.5s ease-in-out'
                      }}
                    ></div>
                  </div>
                  <p className="text-muted small text-end mb-0 mt-1" style={{ fontSize: '0.75rem' }}>
                    {Math.max(0, (weeklyTarget.expected || 0) - (weeklyTarget.achieved || 0))} users to goal
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Third Row: Previous Week's Top Performer */}
      {previousWeekTopPerformer && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border rounded-xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <div className="p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                      <i className="bi bi-trophy-fill text-white" style={{ fontSize: '1.5rem' }}></i>
                    </div>
                    <div>
                      <p className="text-white mb-0 small fw-medium" style={{ fontSize: '0.875rem', opacity: 0.9 }}>Previous Week's Top Performer</p>
                      {previousWeekTopPerformer.weekStart && previousWeekTopPerformer.weekEnd && (
                        <div className="d-flex align-items-center gap-1 mb-1">
                          <i className="bi bi-calendar-event text-white" style={{ fontSize: '0.7rem', opacity: 0.8 }}></i>
                          <small className="text-white" style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            {formatWeekRange(previousWeekTopPerformer.weekStart, previousWeekTopPerformer.weekEnd)}
                          </small>
                        </div>
                      )}
                      <h3 className="text-white mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>
                        {previousWeekTopPerformer.name}
                      </h3>
                    </div>
                  </div>
                  <div className="text-end">
                    <p className="text-white mb-1 small fw-medium" style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Earnings</p>
                    <h2 className="text-white mb-0 fw-bold" style={{ fontSize: '2rem' }}>
                      ₹{previousWeekTopPerformer.totalEarnings?.toLocaleString('en-IN') || 0}
                    </h2>
                    <p className="text-white mb-0 small mt-1" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                      {previousWeekTopPerformer.usersOnboarded || 0} users onboarded
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Last 4 Weeks Chart */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border rounded-xl">
            <div className="p-4">
              <h5 className="fw-bold mb-3">Last 4 Weeks</h5>
              {last4Weeks && last4Weeks.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={last4Weeks}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#4C1D95"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Users Onboarded"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted text-center py-4">No data available for the last 4 weeks</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AmbassadorOverview;

